import { useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import {
  pendingConflicts, resolveAll, resolveConflict, type Choice, type FieldDiff,
} from '../db/conflicts'
import { formatAriary } from '../lib/money'
import { formatDay } from '../lib/dates'
import { Icon } from './Icon'
import './ConflictsSheet.css'

/**
 * Trancher les désaccords — décision 2026-08-24 (soir).
 *
 * Le moteur détectait et stockait les conflits depuis le début, mais rien ne
 * les présentait : ils s'accumulaient dans une table que personne ne lisait.
 * Sur une app d'argent, un désaccord qu'on ne montre pas est un désaccord
 * tranché au hasard.
 *
 * L'écran ne montre que les champs QUI DIFFÈRENT. Afficher les deux lignes
 * entières obligerait à comparer trente valeurs à l'œil pour en trouver une —
 * un écran qui demande ce travail est un écran qu'on referme.
 */
export function ConflictsSheet({ onClose }: { onClose: () => void }) {
  const conflicts = useLiveQuery(() => pendingConflicts(), [], undefined)
  const [occupe, setOccupe] = useState(false)

  if (!conflicts) return null

  async function trancher(id: string, choice: Choice) {
    setOccupe(true)
    try { await resolveConflict(id, choice) } finally { setOccupe(false) }
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true"
         aria-label="Désaccords à trancher">
      <div className="sheet conflicts-sheet">
        <header className="sheet-head">
          <h2>À trancher</h2>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="Fermer">
            <Icon name="close" size={20} />
          </button>
        </header>

        <div className="conflicts-body">
          {conflicts.length === 0 ? (
            <p className="conflicts-empty">
              <Icon name="check" size={16} /> Plus aucun désaccord.
            </p>
          ) : (
            <>
              <p className="conflicts-intro">
                La même donnée a été modifiée à deux endroits. Rien n'est perdu :
                choisis celle qui est juste.
              </p>

              <ul className="conflicts-list">
                {conflicts.map(({ conflict, subject, differences }) => (
                  <li key={conflict.id} className="conflict">
                    <div className="conflict-head">
                      <strong>{subject}</strong>
                      <span>{formatDay(conflict.detected_at.slice(0, 10))}</span>
                    </div>

                    {differences.length === 0 ? (
                      <p className="conflict-note">
                        Les deux versions se ressemblent, seul le moment de
                        modification diffère.
                      </p>
                    ) : (
                      <table className="conflict-diff">
                        <thead>
                          <tr>
                            <th scope="col"><span className="visually-hidden">Champ</span></th>
                            <th scope="col">Sur cet appareil</th>
                            <th scope="col">Ailleurs</th>
                          </tr>
                        </thead>
                        <tbody>
                          {differences.map((d) => (
                            <tr key={d.field}>
                              <th scope="row">{d.label}</th>
                              <td>{render(d, 'mine')}</td>
                              <td>{render(d, 'theirs')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}

                    <div className="conflict-actions">
                      <button type="button" disabled={occupe}
                              onClick={() => void trancher(conflict.id, 'mine')}>
                        Garder celle-ci
                      </button>
                      <button type="button" disabled={occupe}
                              onClick={() => void trancher(conflict.id, 'theirs')}>
                        Prendre l'autre
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Proposé seulement à partir de trois : en dessous, trancher un
                  par un reste plus sûr et ne coûte rien. Jamais coché d'avance —
                  un choix par défaut se valide sans être lu. */}
              {conflicts.length >= 3 && (
                <div className="conflicts-bulk">
                  <p>Tout trancher d'un coup :</p>
                  <div className="conflict-actions">
                    <button type="button" disabled={occupe}
                            onClick={() => { setOccupe(true); void resolveAll('mine').finally(() => setOccupe(false)) }}>
                      Garder celles de cet appareil
                    </button>
                    <button type="button" disabled={occupe}
                            onClick={() => { setOccupe(true); void resolveAll('theirs').finally(() => setOccupe(false)) }}>
                      Prendre celles d'ailleurs
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Une valeur telle qu'on la lit, jamais telle qu'elle est stockée.
 *
 * Un montant en Ariary, une date en toutes lettres, un booléen en oui/non :
 * comparer « 25000 » à « 30000 » demande un effort que l'écran doit éviter à
 * quelqu'un qui doit justement trancher vite et juste.
 */
function render(d: FieldDiff, side: 'mine' | 'theirs') {
  const value = side === 'mine' ? d.mine : d.theirs
  if (value === null || value === undefined || value === '') return <em>vide</em>
  if (typeof value === 'boolean') return value ? 'oui' : 'non'

  if (typeof value === 'number') {
    // Les montants se lisent en Ariary ; une position ou un rang, non.
    const montant = ['amount', 'planned_amount', 'expected_income',
                     'target_amount'].includes(d.field)
    return montant ? formatAriary(value) : String(value)
  }

  const texte = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(texte)) return formatDay(texte)
  if (/^\d{4}-\d{2}-\d{2}T/.test(texte)) return formatDay(texte.slice(0, 10))
  if (texte === 'income') return 'revenu'
  if (texte === 'expense') return 'dépense'
  return texte
}
